import type React from "react";
import { useId, useState } from "react";

// Widget for a paused EHR workflow that needs a clinical decision mid-flight
// (e.g. "which of these 3 matching lab tests?"). Payload-driven and
// EHR-agnostic — the backend decides the copy and which workflow runs; this
// component only collects answers. A paused WorkflowRun sends `questions`, the
// clinician answers, and submit dispatches the `workflow_respond` custom
// action (keyed by run_id). Once answered the widget does NOT collapse: it
// stays in the thread frozen, with the picked option highlighted and every
// control disabled, so scrolling back shows exactly what was chosen. Progress
// and outcome still narrate as assistant messages.
export interface WorkflowQuestion {
  key: string;
  prompt: string;
  options?: string[];
  // When true, the clinician can pick several options (e.g. a panel's tests);
  // the answer for this key is sent as a string[]. Defaults to all options.
  multi?: boolean;
  // Memory-driven pre-pick for a single-select question: `default` is the
  // FULL option string ("id|label") the clinician usually picks, `memory_label`
  // the small tag shown beside it ("your usual"), `memory_entry_id` the memory
  // entry that produced it (so an override can be reported against it).
  default?: string;
  memory_entry_id?: string;
  memory_label?: string;
  // The backend noticed a repeat pick and offers to remember it: one inline
  // "Always pick this?" line whose Yes confirms the pending entry.
  suggest_remember?: { entry_id: string; statement: string; option: string };
  // The clinician may leave this question unanswered: Confirm then sends the
  // reserved SKIP_ANSWER and the workflow leaves that item unplaced (reported
  // as skipped, never as a failure). Absent → the question is required.
  skippable?: boolean;
}

// Reserved answer for a skippable question left unselected. Mirrors
// `superdoc.workflow_prompts.SKIP_ANSWER` on the backend.
export const SKIP_ANSWER = "__skip__";

type Answer = string | string[];

// One answered question of a restored prompt: what was asked and what was
// picked. A 5-question card persists five of these, so history renders five
// rows instead of one row with every answer concatenated into it.
export interface AnsweredQuestion {
  key: string;
  prompt: string;
  answer_label: string;
}

export interface WorkflowPromptData {
  run_id?: string | null;
  title?: string;
  ehr_label?: string;
  question?: string;
  questions?: WorkflowQuestion[];
  // Restored-from-history fields: an answered prompt persists the pick, not
  // the whole form, so it renders as the question plus the chosen label.
  answered?: boolean;
  answer_label?: string;
  answers?: Record<string, Answer>;
  // Per-question history (newer backends). Present ⇒ render one row per
  // question; absent ⇒ the legacy flattened `question`/`answer_label` pair.
  answered_questions?: AnsweredQuestion[];
}

interface WorkflowPromptProps {
  data: WorkflowPromptData;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  disabled?: boolean;
  // This prompt belongs to an earlier turn — render it read-only.
  frozen?: boolean;
}

// The widget only kicks the resume off; progress + outcome stream into the thread.
type Phase = { kind: "input"; questions: WorkflowQuestion[] } | { kind: "submitted" } | { kind: "cancelled" };

const BoltIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path d="M8.5 1.5 3 9h4l-.5 5.5L13 7H8.5l0-5.5Z" stroke="#6366F1" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

// Options are encoded `<value>|<human label>`: the value (segment 0) is what
// the backend parses back off the answer; the label (everything after the
// first `|`) is what the clinician should see — never the raw id. Plain
// options with no `|` (e.g. an AMD panel's "CBC") display as-is.
const optionLabel = (opt: string): string => {
  const idx = opt.indexOf("|");
  return idx === -1 ? opt : opt.slice(idx + 1).trim() || opt;
};

const defaultsFor = (questions: WorkflowQuestion[]): Record<string, Answer> =>
  Object.fromEntries(
    // Multi defaults to ALL options selected (the recommendation implies the
    // whole set). Single starts EMPTY: pre-selecting the first option puts a
    // real order one mis-tap away — seen live with a catalog row literally
    // named "test IGNORE" sitting pre-checked. Confirm stays disabled until
    // the clinician picks. Two exceptions, both of which the clinician can
    // still clear with a click: a remembered `default` (their own recorded
    // choice, and it must still be one of the options), and a question with
    // exactly one option, which is a confirmation rather than a choice.
    questions.map((q) => [q.key, q.multi ? [...(q.options ?? [])] : singleDefault(q)]),
  );

const singleDefault = (q: WorkflowQuestion): string => {
  const d = (q.default ?? "").trim();
  if (d && (!q.options?.length || q.options.includes(d))) return d;
  return q.options?.length === 1 ? q.options[0] : "";
};

const isPicked = (answer: Answer | undefined, opt: string): boolean =>
  Array.isArray(answer) ? answer.includes(opt) : answer === opt;

const WorkflowPrompt: React.FC<WorkflowPromptProps> = ({ data, onCustomAction, disabled, frozen }) => {
  const initialQuestions = data?.questions || [];
  // Radios are grouped by `name` across the whole document. Every card in a
  // thread asks with the same question keys, so a frozen earlier card's checked
  // radio would fight this card's for the group and the fresh pick lost its
  // check (seen live). Scope the group to this card instance.
  const radioScope = useId();
  const [phase, setPhase] = useState<Phase>({ kind: "input", questions: initialQuestions });
  const [answers, setAnswers] = useState<Record<string, Answer>>(() => defaultsFor(initialQuestions));
  // Question keys whose "Always pick this?" / "Use this instead" line has been
  // answered or waved away. Neither line ever gates Confirm.
  const [rememberDone, setRememberDone] = useState<Set<string>>(() => new Set());
  const [overrideDone, setOverrideDone] = useState<Set<string>>(() => new Set());

  // Per-question history, when the backend recorded it. Only meaningful on a
  // restored entry (no live `questions` form), which is the only place it is
  // ever sent.
  const answeredQuestions: AnsweredQuestion[] = initialQuestions.length
    ? []
    : (data?.answered_questions ?? []).filter((q) => !!q && (!!q.prompt?.trim() || !!q.answer_label?.trim()));

  // A restored answered prompt keeps only the question + the pick, so it has
  // no `questions` array — render it anyway; that's the whole point of frozen
  // history. Everything else still needs a live run and a form.
  const answeredHistory = !!data?.answered || !!data?.answer_label || answeredQuestions.length > 0;
  if (!data?.run_id || (!data?.questions?.length && !answeredHistory)) return null;

  const submitAnswers = () => {
    if (disabled || frozen) return;
    // A skippable question left empty is sent as an explicit skip, so the
    // workflow leaves that item unplaced instead of asking again.
    const payload: Record<string, Answer> = { ...answers };
    for (const q of phase.kind === "input" ? phase.questions : []) {
      if (q.skippable && !answered(q)) payload[q.key] = SKIP_ANSWER;
    }
    onCustomAction({
      type: "workflow_respond",
      payload: { run_id: data.run_id, answers: payload },
    });
    setPhase({ kind: "submitted" });
  };

  const cancel = () => {
    if (disabled || frozen) return;
    onCustomAction({ type: "workflow_cancel", payload: { run_id: data.run_id } });
    setPhase({ kind: "cancelled" });
  };

  const confirmRemember = (q: WorkflowQuestion) => {
    if (disabled || !q.suggest_remember) return;
    onCustomAction({
      type: "memory_confirm",
      payload: { entry_id: q.suggest_remember.entry_id, run_id: data.run_id },
    });
    setRememberDone((p) => new Set(p).add(q.key));
  };
  const dismissRemember = (q: WorkflowQuestion) => setRememberDone((p) => new Set(p).add(q.key));

  const overrideDefault = (q: WorkflowQuestion, mode: "once" | "from_now_on") => {
    if (disabled) return;
    onCustomAction({
      type: "memory_override",
      payload: { entry_id: q.memory_entry_id, mode, chosen_option: answers[q.key], run_id: data.run_id },
    });
    setOverrideDone((p) => new Set(p).add(q.key));
  };

  // Read-only whenever the pick is already made — locally (submitted /
  // cancelled) or on a restored prompt from an earlier turn.
  const readOnly = !!frozen || answeredHistory || phase.kind !== "input";
  const caption =
    phase.kind === "submitted"
      ? "On it — I'll keep you posted in the chat."
      : phase.kind === "cancelled"
        ? "Cancelled — nothing was ordered."
        : answeredHistory
          ? "Answered."
          : "";

  // Restored history has no form to freeze — synthesise the row(s) it kept.
  // Per-question history renders below on its own; only the legacy flattened
  // entry needs the single synthesised row.
  const questions: WorkflowQuestion[] =
    phase.kind === "input" && phase.questions.length
      ? phase.questions
      : data.questions?.length
        ? data.questions
        : answeredQuestions.length
          ? []
          : data.answer_label
            ? [{ key: "__answered__", prompt: data.question || "", options: [data.answer_label] }]
            : [];

  // Frozen rows show what was picked: local answers when we collected them,
  // the persisted answers/answer_label when the widget was restored.
  const shownAnswers: Record<string, Answer> = readOnly
    ? data.answers && Object.keys(data.answers).length
      ? data.answers
      : data.answer_label && !data.questions?.length
        ? { __answered__: data.answer_label }
        : answers
    : answers;

  const answered = (q: WorkflowQuestion): boolean => {
    const v = answers[q.key];
    return q.multi ? Array.isArray(v) && v.length > 0 : (v ?? "").toString().trim() !== "";
  };
  // A single-select pick can be cleared by clicking its selected option again
  // (a plain radio group cannot be un-picked, so a pre-selected confirmation
  // would otherwise be impossible to decline).
  const clearIfPicked = (q: WorkflowQuestion, opt: string) => {
    if (readOnly || q.multi || answers[q.key] !== opt) return;
    setAnswers((p) => ({ ...p, [q.key]: "" }));
  };
  const toggleMulti = (key: string, opt: string) =>
    setAnswers((p) => {
      const cur = Array.isArray(p[key]) ? (p[key] as string[]) : [];
      return { ...p, [key]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });
  // Required questions must be answered; a skippable one may be left empty and
  // is then sent as a skip. Confirm never waits on a question the clinician is
  // allowed to leave blank.
  const skippedQuestions = questions.filter((q) => q.skippable && !answered(q));
  const canSubmit = questions.every((q) => q.skippable || answered(q));

  // The header already shows the question (the backend sets `question` to the
  // first prompt), so don't repeat it as a per-row label below — that's the
  // duplicated "subtitle". Only render a row's prompt when it differs.
  const headerText = (data.question || data.title || "").trim();

  return (
    <div style={{ ...styles.container, ...(readOnly ? styles.frozen : {}) }}>
      {(data.title || data.question) && (
        <div style={styles.header}>
          <BoltIcon />
          <span style={{ ...styles.headerText, ...(readOnly ? styles.frozenText : {}) }}>
            {data.question || data.title}
          </span>
        </div>
      )}
      {answeredQuestions.map((q, i) => (
        // One row per answered question, shaped like a live card frozen
        // in-session: the prompt, then its chosen label highlighted-disabled.
        <div key={q.key || `answered_${i}`} style={styles.qRow}>
          {q.prompt.trim() !== headerText && q.prompt.trim() !== "" && <span style={styles.prompt}>{q.prompt}</span>}
          <div style={styles.checkGroup}>
            <label style={{ ...styles.checkRow, ...styles.checkRowFrozen, ...styles.checkRowPicked }}>
              <input
                type="radio"
                name={`${radioScope}:${q.key || `answered_${i}`}`}
                disabled
                checked
                readOnly
                style={styles.checkInput}
              />
              <span>{q.answer_label}</span>
            </label>
          </div>
        </div>
      ))}
      {questions.map((q) => (
        <div key={q.key} style={styles.qRow}>
          {q.prompt.trim() !== headerText && q.prompt.trim() !== "" && <span style={styles.prompt}>{q.prompt}</span>}
          {q.options?.length ? (
            <div style={styles.checkGroup}>
              {q.options.map((opt) => {
                const picked = isPicked(shownAnswers[q.key], opt);
                return (
                  <label
                    key={opt}
                    style={{
                      ...styles.checkRow,
                      ...(readOnly ? styles.checkRowFrozen : {}),
                      ...(readOnly && picked ? styles.checkRowPicked : {}),
                      ...(readOnly && !picked ? styles.checkRowUnpicked : {}),
                    }}
                  >
                    <input
                      type={q.multi ? "checkbox" : "radio"}
                      name={`${radioScope}:${q.key}`}
                      disabled={disabled || readOnly}
                      checked={picked}
                      onClick={() => clearIfPicked(q, opt)}
                      onChange={() => {
                        if (readOnly) return;
                        if (q.multi) {
                          toggleMulti(q.key, opt);
                        } else {
                          setAnswers((p) => ({ ...p, [q.key]: opt }));
                        }
                      }}
                      style={styles.checkInput}
                    />
                    <span>{optionLabel(opt)}</span>
                    {!!q.memory_label && opt === singleDefault(q) && (
                      <span style={styles.memoryTag}>{q.memory_label}</span>
                    )}
                  </label>
                );
              })}
            </div>
          ) : (
            <input
              type="text"
              disabled={disabled || readOnly}
              value={((shownAnswers[q.key] as string) ?? "") || ""}
              onChange={(e) => setAnswers((p) => ({ ...p, [q.key]: e.target.value }))}
              style={styles.input}
            />
          )}
          {!readOnly && !!q.skippable && !answered(q) && (
            <span style={styles.skipNote}>No selection — this item will be skipped.</span>
          )}
          {!readOnly && !!q.suggest_remember && !rememberDone.has(q.key) && (
            <div style={styles.memoryLine}>
              <span>Always pick this?</span>
              <button type="button" disabled={disabled} onClick={() => confirmRemember(q)} style={styles.linkButton}>
                Yes
              </button>
              <button type="button" disabled={disabled} onClick={() => dismissRemember(q)} style={styles.linkButton}>
                Not now
              </button>
            </div>
          )}
          {!readOnly &&
            !q.multi &&
            !!q.memory_entry_id &&
            !!singleDefault(q) &&
            answered(q) &&
            answers[q.key] !== singleDefault(q) &&
            !overrideDone.has(q.key) && (
              <div style={styles.memoryLine}>
                <span>Use this instead:</span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => overrideDefault(q, "once")}
                  style={styles.linkButton}
                >
                  Just this time
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => overrideDefault(q, "from_now_on")}
                  style={styles.linkButton}
                >
                  From now on
                </button>
              </div>
            )}
        </div>
      ))}
      {readOnly ? (
        caption && <span style={styles.caption}>{caption}</span>
      ) : (
        <div style={styles.actions}>
          <button
            type="button"
            disabled={disabled || !canSubmit}
            onClick={submitAnswers}
            style={{
              ...styles.button,
              ...styles.confirmButton,
              ...(disabled || !canSubmit ? styles.buttonDisabled : {}),
            }}
          >
            Confirm &amp; continue
          </button>
          {skippedQuestions.length > 0 && (
            <span style={styles.caption}>
              {skippedQuestions.length === 1
                ? "1 item will be skipped."
                : `${skippedQuestions.length} items will be skipped.`}
            </span>
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={cancel}
            style={{
              ...styles.button,
              ...styles.cancelButton,
              ...(disabled ? styles.buttonDisabled : {}),
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 14px",
    marginTop: "8px",
    borderRadius: "12px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  frozen: { backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB" },
  frozenText: { color: "#6B7280" },
  caption: { fontSize: "12px", fontWeight: 500, color: "#9CA3AF", lineHeight: 1.4 },
  actions: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" },
  header: { display: "flex", alignItems: "center", gap: "8px" },
  headerText: { fontSize: "13px", fontWeight: 600, color: "#111827", lineHeight: 1.4 },
  qRow: { display: "flex", flexDirection: "column", gap: "5px", fontSize: "13px" },
  prompt: { color: "#111827", fontWeight: 500 },
  checkGroup: { display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "2px" },
  // flex-start + a nudged control keep the checkbox on the FIRST line when a
  // long catalog label wraps.
  checkRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "7px",
    fontSize: "13px",
    color: "#374151",
    lineHeight: 1.45,
    cursor: "pointer",
  },
  checkRowFrozen: { cursor: "default" },
  // The pick stays legible in the frozen card; the roads not taken fade.
  checkRowPicked: { color: "#4338CA", fontWeight: 600 },
  checkRowUnpicked: { color: "#9CA3AF" },
  checkInput: { marginTop: "2px", flexShrink: 0, accentColor: "#4F46E5" },
  // "your usual" beside the remembered option — a tag, visibly not part of the label.
  memoryTag: {
    fontSize: "10.5px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "3px 7px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    color: "#4338CA",
    backgroundColor: "#EEF2FF",
    border: "1px solid #C7D2FE",
    marginTop: "1px",
  },
  skipNote: { fontSize: "12px", color: "#9CA3AF", paddingLeft: "2px" },
  memoryLine: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    fontSize: "12px",
    color: "#6B7280",
    paddingLeft: "2px",
  },
  linkButton: {
    padding: 0,
    border: "none",
    background: "none",
    fontSize: "12px",
    fontWeight: 600,
    color: "#4F46E5",
    textDecoration: "underline",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  input: {
    padding: "6px 8px",
    border: "1px solid #D1D5DB",
    borderRadius: "8px",
    fontFamily: "inherit",
    fontSize: "13px",
  },
  button: {
    flex: "0 1 auto",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: "4px",
  },
  confirmButton: { backgroundColor: "#4338CA", color: "#FFFFFF", borderColor: "#4338CA" },
  cancelButton: { backgroundColor: "#FFFFFF", color: "#374151", borderColor: "#D1D5DB" },
  buttonDisabled: { opacity: 0.6, cursor: "not-allowed" },
};

export default WorkflowPrompt;
